/**
 * Decode de JWT sem dependência externa.
 *
 * Isto NÃO é validação de segurança — a assinatura nunca é conferida aqui. O
 * decode serve apenas para descobrir quando o token expira e agendar a
 * renovação; quem valida de fato é a API.
 */

export type JwtPayload = {
  exp?: number;
  sub?: string;
  [chave: string]: any;
};

const base64UrlParaTexto = (base64Url: string): string => {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const preenchido = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "="
  );
  const binario = atob(preenchido);
  // O payload pode conter acentos (nome do usuário), então é preciso
  // reinterpretar os bytes como UTF-8 em vez de latin-1.
  const bytes = Uint8Array.from(binario, (caractere) => caractere.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
};

export const decodeJwt = (token?: string | null): JwtPayload | null => {
  if (!token) return null;
  try {
    const partes = token.split(".");
    if (partes.length !== 3) return null;
    const payload = JSON.parse(base64UrlParaTexto(partes[1]));
    return payload && typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
};

/** Expiração do token em milissegundos (epoch), ou null se não houver claim `exp`. */
export const getTokenExpiryMs = (token?: string | null): number | null => {
  const payload = decodeJwt(token);
  return typeof payload?.exp === "number" ? payload.exp * 1000 : null;
};

export const isTokenExpired = (
  token?: string | null,
  skewMs: number = 0
): boolean => {
  const expiraEm = getTokenExpiryMs(token);
  if (expiraEm === null) return false; // desconhecido: não assume expirado
  return expiraEm - skewMs <= Date.now();
};

/**
 * Expiração da sessão em milissegundos (epoch), a partir do objeto gravado em
 * `@MatriculaWeb:user`.
 *
 * O `expiresAt` devolvido pela API tem precedência: é o instante canônico, o
 * mesmo usado na assinatura do token. O claim `exp` do JWT é o fallback, e
 * cobre sessões abertas contra versões da API anteriores a este campo.
 *
 * `null` significa "desconhecido" — e o chamador deve tratar isso como
 * fail-open: nem renovar, nem deslogar proativamente. O 401 da API resolve.
 */
export const getSessionExpiryMs = (userData: any): number | null => {
  const expiresAt = userData?.expiresAt;
  if (typeof expiresAt === "string" || typeof expiresAt === "number") {
    const parsed = new Date(expiresAt).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }
  return getTokenExpiryMs(userData?.token);
};
